import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActionExecutionResult } from '@rossum/api-client/rulesExecutionLogs';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { TabPanel } from '@rossum/components/tab-panel/TabPanel';
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { DataGridPro, GridRowParams } from '@rossum/ui/x-data-grid-pro';
import JsonEditor from '../../../../components/UI/Editor/components/JsonEditor';
import { camelToSnake, convertKeys } from '../../../../lib/keyConvertor';
import {
  formatToJSONString,
  isLineItemRule,
  TransformedRuleExecutionLog,
} from '../../helpers';
import { useActionColumns } from '../../hooks/useActionColumns';
import {
  LineItemRow,
  useLineItemsColumns,
} from '../../hooks/useLineItemsColumns';

const CODE_EDITOR_HEIGHT = 400;
const EMPTY_STATE_MIN_HEIGHT = 100;
const MAX_PANEL_HEIGHT = 500;

type DetailTab = 'actions' | 'code' | 'condition';

type LineItem = {
  lineIndex: number;
  conditionResult: boolean;
  values: Record<string, string> | null;
  actions: ActionExecutionResult[] | null;
};

export const PanelContent = ({
  row,
}: GridRowParams<TransformedRuleExecutionLog>) => {
  const intl = useIntl();
  const actionsColumns = useActionColumns();
  const lineItemsColumns = useLineItemsColumns();

  // Convert to snake_case for raw API response display
  const snakeCasedLog = convertKeys(camelToSnake)(row);

  // Check if any condition was met across all lines
  const anyConditionMet = row.triggerConditionResults.some(result => result);

  const tabs: DetailTab[] = ['actions', 'condition', 'code'];

  // tabs always has at least 2 elements, so tabs[0] is always defined
  const [currentTab, setCurrentTab] = useState<DetailTab>(tabs[0] ?? 'code');

  const handleChange = (newTab: DetailTab) => setCurrentTab(newTab);

  // Build line items for display
  const lineItems: LineItem[] = useMemo(() => {
    const numLines = row.triggerConditionResults.length;
    return Array.from({ length: numLines }, (_, index) => ({
      lineIndex: index,
      conditionResult: row.triggerConditionResults[index] ?? false,
      values: row.triggerConditionValues?.[index] ?? null,
      actions: row.actions?.[index] ?? null,
    }));
  }, [row]);

  // Prepare actions data for single-line rules (action per row)
  const actionsWithIds = useMemo(() => {
    if (!row.actions || isLineItemRule(row)) return [];
    return row.actions
      .flatMap(lineActions => lineActions ?? [])
      .map((action, index) => ({
        ...action,
        id: action.actionId || `action-${index}`,
      }));
  }, [row]);

  // Prepare line items data for multi-line rules (line per row)
  const lineItemsWithIds = useMemo((): LineItemRow[] => {
    if (!isLineItemRule(row)) return [];
    return lineItems.map(item => ({
      id: `line-${item.lineIndex}`,
      lineIndex: item.lineIndex,
      conditionResult: item.conditionResult,
      values: item.values,
      actions: item.actions,
      actionsText: item.actions?.map(a => a.actionType).join(', ') || '',
      hasError: item.actions?.some(a => !a.success) ?? false,
    }));
  }, [lineItems, row]);

  return (
    <Stack spacing={0.5} p={2}>
      <Stack spacing={1}>
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Tabs
              value={currentTab}
              onChange={(_, value: DetailTab) => handleChange(value)}
            >
              {tabs.map(tab => (
                <Tab
                  key={tab}
                  value={tab}
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {intl.formatMessage({
                        id: `features.rulesExecutionLogs.detail.${tab}.tab`,
                      })}
                    </Typography>
                  }
                />
              ))}
            </Tabs>
          </Stack>
          <Divider />
        </Stack>
        <Stack component={Paper}>
          {/* Tab: Actions - Sub-table with action execution results */}
          <TabPanel active={currentTab === 'actions'} tabId="actions">
            {!anyConditionMet ? (
              <Box
                sx={{
                  minHeight: EMPTY_STATE_MIN_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({
                    id: 'features.rulesExecutionLogs.detail.actions.conditionNotMet',
                  })}
                </Typography>
              </Box>
            ) : isLineItemRule(row) ? (
              // Multi-line rule: Show line items grid
              lineItemsWithIds.length > 0 ? (
                <Box sx={{ maxHeight: MAX_PANEL_HEIGHT, overflow: 'auto' }}>
                  <DataGridPro
                    rows={lineItemsWithIds}
                    columns={lineItemsColumns}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableColumnResize
                    disableColumnReorder
                    hideFooter
                    autoHeight
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 1,
                        borderColor: 'divider',
                      },
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    minHeight: EMPTY_STATE_MIN_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({
                      id: 'features.rulesExecutionLogs.detail.actions.empty',
                    })}
                  </Typography>
                </Box>
              )
            ) : // Single-line rule: Show actions grid
            actionsWithIds.length > 0 ? (
              <Box sx={{ maxHeight: MAX_PANEL_HEIGHT, overflow: 'auto' }}>
                <DataGridPro
                  rows={actionsWithIds}
                  columns={actionsColumns}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  disableColumnResize
                  disableColumnReorder
                  hideFooter
                  autoHeight
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      borderBottom: 1,
                      borderColor: 'divider',
                    },
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  minHeight: EMPTY_STATE_MIN_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({
                    id: 'features.rulesExecutionLogs.detail.actions.empty',
                  })}
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* Tab: Code - Formatted JSON of entire log */}
          <TabPanel active={currentTab === 'code'} tabId="code">
            <Box sx={{ position: 'relative' }}>
              <JsonEditor
                name="code"
                value={formatToJSONString(snakeCasedLog)}
                debounceChangePeriod={0}
                height={CODE_EDITOR_HEIGHT}
                readonly
                mode="json"
              />
              <Box sx={{ position: 'absolute', right: 24, bottom: 24 }}>
                <CopyToClipboardButton
                  content={formatToJSONString(snakeCasedLog)}
                  iconColor="action"
                />
              </Box>
            </Box>
          </TabPanel>

          {/* Tab: Condition - Show condition details */}
          <TabPanel active={currentTab === 'condition'} tabId="condition">
            <Stack
              spacing={2}
              p={2}
              sx={{ maxHeight: MAX_PANEL_HEIGHT, overflow: 'auto' }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {intl.formatMessage({
                    id: 'features.rulesExecutionLogs.detail.condition.condition',
                  })}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    borderRadius: borderRadius('s'),
                    fontFamily: "'Roboto Mono Variable', monospace",
                    fontSize: '1.2rem',
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {row.triggerCondition}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {intl.formatMessage({
                    id: 'features.rulesExecutionLogs.detail.condition.result',
                  })}
                </Typography>
                {isLineItemRule(row) ? (
                  <Typography variant="body2" color="text.secondary">
                    {row.triggerConditionResults.filter(Boolean).length} /{' '}
                    {row.triggerConditionResults.length}{' '}
                    {intl
                      .formatMessage({
                        id: 'features.rulesExecutionLogs.triggerResult.true',
                      })
                      .toLowerCase()}
                  </Typography>
                ) : (
                  <Chip
                    label={
                      row.triggerConditionResults[0]
                        ? intl.formatMessage({
                            id: 'features.rulesExecutionLogs.triggerResult.true',
                          })
                        : intl.formatMessage({
                            id: 'features.rulesExecutionLogs.triggerResult.false',
                          })
                    }
                    size="small"
                    color={
                      row.triggerConditionResults[0] ? 'success' : 'default'
                    }
                  />
                )}
              </Box>
              {row.triggerConditionValues &&
                row.triggerConditionValues.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      {intl.formatMessage({
                        id: 'features.rulesExecutionLogs.detail.condition.values',
                      })}
                    </Typography>
                    <JsonEditor
                      name="conditionValues"
                      value={formatToJSONString(
                        isLineItemRule(row)
                          ? row.triggerConditionValues
                          : row.triggerConditionValues[0]
                      )}
                      debounceChangePeriod={0}
                      height={180}
                      readonly
                      mode="json"
                    />
                  </Box>
                )}
            </Stack>
          </TabPanel>
        </Stack>
      </Stack>
    </Stack>
  );
};
